import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { PageSize } from "veneer/DataTable";
import Tabs, { Tab, TabRow } from "veneer/Tabs";

import { useFeatureFlag } from "cortex/ui/src/hooks";
import { HeaderTabs } from "cortex/ui/src/view/layout";
import { Incident } from "cortex/ui/src/zeus/view/incidents/model";

import IncidentsTable from "./IncidentsTable";

interface TabbedIncidentsTableProps {
  sizePerPage?: PageSize;
  storesLoaded: boolean;
  tableData: ReadonlyArray<Incident>;
}

export default function TabbedIncidentsTable({
  sizePerPage = 10,
  storesLoaded,
  tableData,
}: TabbedIncidentsTableProps) {
  const history = useHistory();
  const { incidentId } = useParams<{ incidentId?: string }>();
  const isGroupedTriggeredAlertsEnabled = useFeatureFlag(
    "is-grouped-triggered-alerts",
  );
  const [activeTab, setActiveTab] = useState("unresolved");

  const routeSelectedIncident = useMemo(() => {
    if (!isGroupedTriggeredAlertsEnabled || !incidentId) {
      return null;
    }

    return tableData.find(({ id }) => String(id) === incidentId) ?? null;
  }, [incidentId, isGroupedTriggeredAlertsEnabled, tableData]);

  const handleOpenIncident = useCallback(
    (incidentIdToOpen: number) => {
      history.push(`/diagnostics/incidents/${incidentIdToOpen}`);
    },
    [history],
  );

  const handleCloseIncident = useCallback(() => {
    history.push("/diagnostics/incidents");
  }, [history]);

  useEffect(() => {
    if (
      isGroupedTriggeredAlertsEnabled &&
      incidentId &&
      storesLoaded &&
      routeSelectedIncident === null
    ) {
      history.replace("/diagnostics/incidents");
    }
  }, [
    history,
    incidentId,
    isGroupedTriggeredAlertsEnabled,
    routeSelectedIncident,
    storesLoaded,
  ]);

  useEffect(() => {
    if (routeSelectedIncident) {
      setActiveTab(routeSelectedIncident.resolved ? "resolved" : "unresolved");
    }
  }, [routeSelectedIncident]);

  const filteredTableData = useMemo(() => {
    return (tableData || []).filter((incident) => {
      const isResolved = !!incident?.resolved;
      return activeTab === "resolved" ? isResolved : !isResolved;
    });
  }, [activeTab, tableData]);

  return (
    <>
      <HeaderTabs>
        <TabRow />
        <Tabs key={activeTab}>
          <Tab
            defaultSelected={activeTab === "unresolved"}
            label={isGroupedTriggeredAlertsEnabled ? "Open" : "Unresolved"}
            onClick={() => setActiveTab("unresolved")}
          />
          <Tab
            defaultSelected={activeTab === "resolved"}
            label="Resolved"
            onClick={() => setActiveTab("resolved")}
          />
        </Tabs>
      </HeaderTabs>
      <IncidentsTable
        incidents={filteredTableData}
        onCloseIncident={handleCloseIncident}
        onOpenIncident={handleOpenIncident}
        routeSelectedIncident={routeSelectedIncident}
        showFilters
        sizePerPage={sizePerPage}
        storesLoaded={storesLoaded}
        type={activeTab === "resolved" ? "resolved" : "unresolved"}
      />
    </>
  );
}
